import {Box, Group, HoverCard, Stack, Text, ThemeIcon, UnstyledButton} from '@mantine/core'
import {IconAlertTriangle} from '@tabler/icons-react'
import {format, parseISO} from 'date-fns'

import {commitmentBandColor} from 'src/theme/semantic-colors'

import type {CommitmentDay} from './schemas'

const BAR_HEIGHT = 140
const DAY_MIN_WIDTH = 52
/** Gap between stacked segments, taken out of each segment so bars stay to scale. */
const SEGMENT_GAP = 1

type StockCommitmentChartProps = {
    days: CommitmentDay[]
    totalCount: number
}

/** Ordered by certainty. Bars stack these from the baseline up, so the bar renders reversed. */
const bands = [
    {key: 'on_site', label: 'On site', hint: 'or in transit · no known return'},
    {key: 'awaiting_deployment', label: 'Awaiting deployment', hint: 'releases on its end date'},
    {key: 'free', label: 'Free', hint: undefined},
] as const

type BandKey = (typeof bands)[number]['key']

function bandCount(day: CommitmentDay, key: BandKey) {
    switch (key) {
        case 'on_site':
            return day.onSiteCount
        case 'awaiting_deployment':
            return day.awaitingDeploymentCount
        case 'free':
            return day.freeCount
    }
}

function dayLabel(day: CommitmentDay) {
    return `${format(parseISO(day.date), 'EEEE, MMM d, yyyy')}. On site ${day.onSiteCount}, awaiting deployment ${day.awaitingDeploymentCount}, free ${day.freeCount}.`
}

function CommitmentDayDetails({day, totalCount}: {day: CommitmentDay; totalCount: number}) {
    return (
        <Stack gap={4}>
            <Text size="sm" fw={600}>
                {format(parseISO(day.date), 'EEEE, MMM d, yyyy')}
            </Text>
            <Text size="sm">On site: {day.onSiteCount}</Text>
            <Text size="sm">Awaiting deployment: {day.awaitingDeploymentCount}</Text>
            <Text size="sm">Free: {day.freeCount}</Text>
            <Text size="sm" c="dimmed">
                Committed of {totalCount}: {day.committedCount}
            </Text>
            {day.overCapacityCount > 0 && (
                <Text size="sm" c="orange.8" fw={600}>
                    Over-committed by {day.overCapacityCount}
                </Text>
            )}
        </Stack>
    )
}

function CommitmentDayColumn({
    day,
    totalCount,
    isToday,
}: {
    day: CommitmentDay
    totalCount: number
    isToday: boolean
}) {
    // Over-committed days fill to the capacity line; the flag carries the overflow.
    const scale = Math.max(totalCount, day.committedCount, 1)
    const barBands = [...bands].reverse()

    return (
        <Box
            data-testid={`commitment-day-${day.date}`}
            style={{
                minWidth: DAY_MIN_WIDTH,
                flex: `1 0 ${DAY_MIN_WIDTH}px`,
                padding: '0 3px',
                borderRadius: 6,
                backgroundColor: isToday ? 'var(--mantine-color-gray-1)' : undefined,
                boxShadow: isToday ? 'inset 0 0 0 1px var(--mantine-color-gray-4)' : undefined,
            }}
        >
            <HoverCard width={240} shadow="sm" withArrow openDelay={200} closeDelay={100}>
                <HoverCard.Target>
                    <UnstyledButton
                        aria-label={dayLabel(day)}
                        style={{width: '100%', height: BAR_HEIGHT, display: 'block'}}
                    >
                        <Box style={{height: '100%', display: 'flex', flexDirection: 'column'}}>
                            {barBands.map((band, index) => {
                                const count = bandCount(day, band.key)
                                return (
                                    <Box
                                        key={band.key}
                                        data-testid={`commitment-segment-${band.key}`}
                                        data-count={count}
                                        bg={commitmentBandColor(band.key)}
                                        style={{
                                            height: `${(count / scale) * 100}%`,
                                            flexShrink: 0,
                                            boxSizing: 'border-box',
                                            // Gaps are surface, so boundaries read without a border.
                                            borderTop:
                                                index === 0
                                                    ? undefined
                                                    : `${SEGMENT_GAP}px solid var(--mantine-color-body)`,
                                            borderRadius:
                                                index === 0
                                                    ? '4px 4px 2px 2px'
                                                    : index === barBands.length - 1
                                                      ? '2px 2px 4px 4px'
                                                      : 2,
                                        }}
                                    />
                                )
                            })}
                        </Box>
                    </UnstyledButton>
                </HoverCard.Target>
                <HoverCard.Dropdown p="sm">
                    <CommitmentDayDetails day={day} totalCount={totalCount} />
                </HoverCard.Dropdown>
            </HoverCard>
            <Text
                size="xs"
                fw={isToday ? 600 : undefined}
                c={isToday ? 'dark' : 'dimmed'}
                ta="center"
                mt={8}
            >
                {isToday ? 'Today' : format(parseISO(day.date), 'EEE')}
            </Text>
            <Text size="xs" c="dimmed" ta="center">
                {format(parseISO(day.date), 'M/d')}
            </Text>
            {day.overCapacityCount > 0 && (
                <Stack align="center" gap={2} mt={4}>
                    <ThemeIcon color="orange.8" variant="transparent" size="sm" aria-hidden="true">
                        <IconAlertTriangle size={14} />
                    </ThemeIcon>
                    <Text size="xs" c="orange.8" ta="center" lh={1.1}>
                        Over by {day.overCapacityCount}
                    </Text>
                </Stack>
            )}
        </Box>
    )
}

/**
 * Today's split plus the days ahead: how much of the pool is promised, and when it
 * frees up. The first day's free count is the number the assign modal will offer.
 */
export function StockCommitmentChart({days, totalCount}: StockCommitmentChartProps) {
    const today = days.at(0)

    return (
        <Stack gap="sm">
            <Group justify="space-between" align="flex-end" wrap="wrap" gap="md">
                <Stack gap={6}>
                    {today && (
                        <Group gap={8} align="baseline">
                            <Text fz={30} fw={700} lh={1} data-testid="stock-free-today">
                                {today.freeCount}
                            </Text>
                            <Text c="dimmed">of {totalCount} free to assign today</Text>
                        </Group>
                    )}
                    <Group gap="lg" wrap="wrap">
                        {bands.map((band) => (
                            <Group key={band.key} gap={7} wrap="nowrap">
                                <Box
                                    bg={commitmentBandColor(band.key)}
                                    style={{width: 11, height: 11, borderRadius: 3, flexShrink: 0}}
                                />
                                <Text size="sm">
                                    {band.label}{' '}
                                    <Text span fw={600}>
                                        {today ? bandCount(today, band.key) : 0}
                                    </Text>
                                    {band.hint && (
                                        <Text span c="dimmed">
                                            {' '}
                                            · {band.hint}
                                        </Text>
                                    )}
                                </Text>
                            </Group>
                        ))}
                    </Group>
                </Stack>
                <Group gap={7} wrap="nowrap">
                    <Box
                        style={{
                            width: 16,
                            borderTop: '2px dashed var(--mantine-color-gray-4)',
                            flexShrink: 0,
                        }}
                    />
                    <Text size="sm" c="dimmed">
                        Stock level {totalCount}
                    </Text>
                </Group>
            </Group>

            <Box style={{overflowX: 'auto'}}>
                <Box style={{minWidth: days.length * DAY_MIN_WIDTH, position: 'relative'}}>
                    <Box
                        aria-label={`Stock capacity: ${totalCount}`}
                        data-testid="commitment-capacity-line"
                        style={{
                            position: 'absolute',
                            left: 0,
                            right: 0,
                            top: 0,
                            borderTop: '2px dashed var(--mantine-color-gray-4)',
                            zIndex: 1,
                            pointerEvents: 'none',
                        }}
                    />
                    <Group align="flex-start" gap={4} wrap="nowrap">
                        {days.map((day, index) => (
                            <CommitmentDayColumn
                                key={day.date}
                                day={day}
                                totalCount={totalCount}
                                isToday={index === 0}
                            />
                        ))}
                    </Group>
                </Box>
            </Box>
        </Stack>
    )
}
